import React, { useState, useEffect, useCallback, useRef } from "react";
import apiClient from "../../api_client";
import { Table, Checkbox, message, Card, Row, Col, Input } from "antd";
import { API_URL } from "../../url_config";
import {NavigationStepsCap} from "../../components/navbars/navbar_cap"
import { Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import ModifyItemPage from "../../components/modify_page";
import CoquillesTable from "../../components/counter";
const { TextArea } = Input;

// === Style du tableau ===
const tableStyle = document.createElement("style");
tableStyle.innerHTML = `
  .custom-row td {
    border-bottom: 2px solid #d9d9d9 !important;
  }

  .ant-table-thead > tr > th {
    background-color: #f5f5f5 !important;
    font-weight: 600;
    border-bottom: 2px solid #bfbfbf !important;
    color: #333;
  }

  .ant-table-tbody > tr:hover > td {
    background-color: #f0f0f0 !important;
  }

  .ant-table {
    border-collapse: separate !important;
    border-spacing: 0;
  }
`;
document.head.appendChild(tableStyle);

// === Composant interne (zones texte : Commentaire + Aide) ===
function TableTetra({ record, onChange }) {
  const [comment, setComment] = useState(record.comment || "");
  const [aide, setAide] = useState(record.aide || "");

  useEffect(() => {
    setComment(record.comment || "");
    setAide(record.aide || "");
  }, [record.comment, record.aide]);

  const handlePatch = (field, value) => {
    onChange(record.key, field, value);
  };

  return (
    <div style={{ padding: "20px" }}>
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Commentaire" bordered>
            <TextArea
              rows={4}
              value={comment}
              onChange={(e) => {
                const value = e.target.value;
                setComment(value);
                handlePatch("comment", value);
              }}
              placeholder="Saisis un commentaire..."
            />
          </Card>
        </Col>

        <Col span={12}>
          <Card
            title={
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span>Informations</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                (Supervision)
              </span>
              </div>
            }
            bordered
          >
            <TextArea
              rows={4}
              value={aide}
              onChange={(e) => {
                const value = e.target.value;
                setAide(value);
                handlePatch("aide", value);
              }}
              placeholder="Décris ton besoin d’aide..."
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}

// === Composant principal ===
const ValidationTetraCap = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const pendingUpdatesRef = useRef(new Map());
  const timeoutRefs = useRef(new Map());
  const isMountedRef = useRef(true);

  // Fonction pour charger les données
  const fetchData = useCallback(async () => {
    try {
      const res = await apiClient.get(
        `${API_URL}/validations/validation_tetra/?ot_tag=cap`
      );
      if (!isMountedRef.current) return;

        const formatted = res.data.map((item, index) => ({
          key: index,
          id: item.id,
          // ot: item.ot,
          coquille: item.coquille,
          test_ad: item.test_ad,
          remontee_de_ad_tetra: item.remontee_de_ad_tetra,
          ecoute_ambiance: item.ecoute_ambiance,
          prise_en_compte_cloture_appel: item.prise_en_compte_cloture_appel,
          pre_diagnostic_si_incident: item.pre_diagnostic_si_incident,
          comment: item.comment,
          aide: item.aide,
          signal: item.signal,
        }));

        setData(formatted);
      } catch (error) {
        console.error("Erreur de chargement :", error);
        message.error("Erreur de chargement des données");
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
  }, []);

  // Charger les données au montage uniquement
  useEffect(() => {
    isMountedRef.current = true;
    setLoading(true);
    fetchData();

    return () => {
      isMountedRef.current = false;
    };
  }, [fetchData]);

  // Fonction pour envoyer la mise à jour au serveur
  const sendUpdate = async (updateKey) => {
    const update = pendingUpdatesRef.current.get(updateKey);
    if (!update) return;

    try {
      await apiClient.patch(
        `${API_URL}/validations/validation_tetra/${update.id}/`,
        { [update.field]: update.value }
      );

      const currentUpdate = pendingUpdatesRef.current.get(updateKey);
      if (currentUpdate && currentUpdate.timestamp === update.timestamp) {
        pendingUpdatesRef.current.delete(updateKey);
      }
    } catch (error) {
      console.error(error);
      message.error("Erreur de mise à jour");
    }
  };

  // ✅ Mise à jour locale + serveur avec debounce
  const handleDataChange = (key, field, value) => {
    let itemId;

    setData((prev) =>
      prev.map((item) => {
        if (item.key === key) {
          itemId = item.id;
          return { ...item, [field]: value };
        }
        return item;
      })
    );

    if (itemId) {
      const updateKey = `${itemId}_${field}`;
      
      const existingTimeout = timeoutRefs.current.get(updateKey);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      pendingUpdatesRef.current.set(updateKey, {
        id: itemId,
        field,
        value,
        timestamp: Date.now()
      });

      const timeoutId = setTimeout(() => {
        sendUpdate(updateKey);
        timeoutRefs.current.delete(updateKey);
      }, 1000);

      timeoutRefs.current.set(updateKey, timeoutId);
    }
  };

  // Filtrer les données selon la recherche
  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      (item.coquille?.toString().toLowerCase() || "").includes(search) ||
      (item.comment?.toString().toLowerCase() || "").includes(search) ||
      (item.aide?.toString().toLowerCase() || "").includes(search)
    );
  });

  // === Colonnes du tableau ===
  const columns = [
    /*{
      title: "OT",
      dataIndex: "ot",
      key: "ot",
    },
    */
    {
      title: "Coquille",
      dataIndex: "coquille",
      key: "coquille",
      sorter: (a, b) => (a.coquille || "").localeCompare(b.coquille || ""),
    },
    {
      title: "Test AD",
      dataIndex: "test_ad",
      key: "test_ad",
      sorter: (a, b) => (a.test_ad === b.test_ad ? 0 : a.test_ad ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "test_ad", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
          className="custom-checkbox-border"
        />
      ),
    },
    {
      title: "Remontée de AD TETRA",
      dataIndex: "remontee_de_ad_tetra",
      key: "remontee_de_ad_tetra",
      sorter: (a, b) => (a.remontee_de_ad_tetra === b.remontee_de_ad_tetra ? 0 : a.remontee_de_ad_tetra ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "remontee_de_ad_tetra", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
          className="custom-checkbox-border"
        />
      ),
    },
    {
      title: "Écoute ambiance",
      dataIndex: "ecoute_ambiance",
      key: "ecoute_ambiance",
      sorter: (a, b) => (a.ecoute_ambiance === b.ecoute_ambiance ? 0 : a.ecoute_ambiance ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "ecoute_ambiance", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
          className="custom-checkbox-border"
        />
      ),
    },
    {
      title: "Prise en compte clôture appel",
      dataIndex: "prise_en_compte_cloture_appel",
      key: "prise_en_compte_cloture_appel",
      sorter: (a, b) => (a.prise_en_compte_cloture_appel === b.prise_en_compte_cloture_appel ? 0 : a.prise_en_compte_cloture_appel ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(
              record.key,
              "prise_en_compte_cloture_appel",
              e.target.checked
            )
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
          className="custom-checkbox-border"
        />
      ),
    },
    {
      title: "Pré-diagnostic si incident",
      dataIndex: "pre_diagnostic_si_incident",
      key: "pre_diagnostic_si_incident",
      sorter: (a, b) => (a.pre_diagnostic_si_incident === b.pre_diagnostic_si_incident ? 0 : a.pre_diagnostic_si_incident ? -1 : 1),
      render: (val, record) => (
        <Checkbox
          checked={val}
          onChange={(e) =>
            handleDataChange(record.key, "pre_diagnostic_si_incident", e.target.checked)
          }
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 auto",
            transform: "scale(1.3)",
            cursor: "pointer",
            accentColor: "#007bff",
          }}
          className="custom-checkbox-border"
        />
      ),
    },
    {
  title: "ETAT",
  dataIndex: "signal",
  sorter: (a, b) => a.signal - b.signal,
  render: (val, record) => {
    // 0 = pas encore choisi, 1 = validé, -1 = non validé
    const color =
      val === 1 ? "#4CAF50" : val === -1 ? "#FF4D4F" : "#BFBFBF";
    const symbol = val === 1 ? "✓" : val === -1 ? "✗" : " ";

    const handleClick = () => {
      // Cycle entre 0 → 1 → -1 → 0 ...
      const nextVal = val === 0 ? 1 : val === 1 ? -1 : 0;
      handleDataChange(record.key, "signal", nextVal);
    };

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: "pointer",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: "6px",
            backgroundColor: color,
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
            fontSize: "16px",
            userSelect: "none",
            transition: "all 0.3s ease",
          }}
        >
          {symbol}
        </div>
      </div>
    );
  },
},
  ];

  return (
    <div style={{ padding: "20px" }}>
    <NavigationStepsCap selectedKey="tetra" />
            <CoquillesTable 
              url={`${API_URL}/validations/counter/tetra/?ot_tag=cap`}
            />
      <Input
        placeholder="Rechercher dans toutes les colonnes..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ 
          marginBottom: "16px", 
          width: "100%",
          maxWidth: "500px"
        }}
        allowClear
      />

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <TableTetra
              record={record}
              onChange={handleDataChange}
            />
          ),
        }}
        bordered
        style={{
          border: "2px solid #bfbfbf",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
        }}
        rowClassName={() => "custom-row"}
pagination={false}
      />

      <div style={{ padding: 20 }}>

            <ModifyItemPage
              apiUrl={`${API_URL}/validations/validation_tetra/`}
              ot_tag="cap"
              title="Gestion"
              rowKey="id"
              trigger={<Button icon={<EditOutlined />} shape="circle" />}
              columns={[
                // { title: "OT", dataIndex: "ot", key: "ot" },
                { title: "Coquille", dataIndex: "coquille", key: "coquille" },
              ]}
              formFields={[
                // { name: "ot", label: "OT", placeholder: "Entrez l'OT" },
                { name: "coquille", label: "Coquille", placeholder: "Entrez la coquille" },
              ]}
              onRefresh={fetchData}
            />
      </div>


    </div>
  );
};

export default ValidationTetraCap;
